/* ============================================================================
   Carta OGE — MBB-BK 117 B-2
   ----------------------------------------------------------------------------
   ORIGEN

   Digitalizada del nomograma "HOVER CEILING OUT OF GROUND EFFECT" del RFM, que
   se publica como un haz de líneas de altitud presión sobre ejes OAT (°C) y
   peso bruto (kg). El operador entregó las coordenadas de cada línea en
   `Tablas_20-08-2026.xlsx`, hoja `Coordenadas_Lineas`.

   La aplicación consulta cartas como rejilla altitud × temperatura, así que las
   líneas se convirtieron a rejilla interpolando cada una en las temperaturas de
   la tabla. Las temperaturas NO son un paso redondo: son la unión de los quiebres
   reales de las líneas con una grilla de 5 °C. Con un paso redondo, la cresta de
   cada línea —que en varias cae en -21 °C o -19 °C— se redondeaba y el nomograma
   perdía justamente la forma que lo hace distinto de una tabla.

   VALIDACIÓN

   El propio gráfico trae un ejemplo de lectura: 15 °C y 8000 ft dan 3100 kg.
   Esta rejilla devuelve 3097 kg. Tres kilos de diferencia sobre una lectura
   hecha a ojo en un gráfico: la conversión reproduce la carta. El test
   `bk117OgeCarta.test.js` lo comprueba y falla si alguien la rompe.

   ESTADO: NO VERIFICADA celda por celda contra el RFM.

   No corrijas un valor "porque parece mal". El único criterio válido para
   cambiar un número de acá es leerlo del manual. Ver docs/decisiones.md AD-005.
   ========================================================================== */

/**
 * Altitudes presión de la rejilla, en pies.
 *
 * El nomograma empieza en 1000 ft. La fila de 0 ft es una COPIA de la de
 * 1000 ft, no una extrapolación: a menor altitud el helicóptero rinde más, así
 * que usar los números de 1000 ft al nivel del mar subestima la capacidad real.
 * El error va hacia el lado seguro. Sin esta fila, cualquier operación cerca
 * del nivel del mar —Talara, Piura, la costa— quedaba "fuera de carta".
 */
export const BK117_OGE_ALTS = [0, 1000, 2000, 3000, 4000, 5000, 6000, 7000, 8000, 9000, 10000, 11000, 12000, 13000, 14000, 15000];

/**
 * Temperaturas de la rejilla, en °C.
 *
 * Unión de los quiebres reales de las líneas con una grilla de 5 °C, para que
 * cada punto publicado del nomograma caiga EXACTO en una columna.
 */
export const BK117_OGE_TEMPS = [-45.0, -40, -35, -30, -25, -21.0, -20.0, -19.0, -18.0, -15.0, -14.5, -12.0, -11.5, -10, -5.0, -3.0, 0, 3.0, 5.0, 6.0, 8.0, 10, 14.0, 15.0, 18.0, 20, 24.0, 25, 26.0, 27.0, 28.0, 30.0, 32.5, 33.0, 34.0, 35, 36.0, 38.0, 39.0, 40.0, 42.0, 43.0, 44.0, 45, 46.0, 47.5, 50];

/**
 * Peso bruto máximo para estacionario fuera del efecto suelo, en kg.
 *
 * `null` = fuera de la envolvente publicada. No significa "no calculado":
 * significa que el nomograma no llega ahí, y la aplicación tiene que decirlo
 * en vez de inventar un número.
 */
export const BK117_OGE_ROWS = [
  /*      0 ft */ [3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3347,3344,3340,null],
  /*   1000 ft */ [3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3347,3344,3340,null],
  /*   2000 ft */ [3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3344,3342,3340,3338,3335,3330,3328,3325,3320,3318,3315,3312,3310,null,null],
  /*   3000 ft */ [3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3344,3332,3329,3326,3323,3320,3314,3306,3305,3302,3299,3296,3290,3287,3284,3278,3275,null,null,null,null,null],
  /*   4000 ft */ [3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3344,3338,3326,3323,3314,3308,3295,3292,3289,3286,3283,3277,3270,3268,3265,3262,3259,3253,3250,3237,3210,null,null,null,null,null,null],
  /*   5000 ft */ [3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3350,3344,3335,3326,3320,3317,3311,3305,3292,3289,3280,3274,3262,3259,3256,3253,3250,3244,3237,3235,3227,3218,3210,3170,3150,3130,null,null,null,null,null,null,null],
  /*   6000 ft */ [3310,3318,3327,3335,3343,3350,3348,3347,3345,3336,3334,3327,3326,3321,3306,3300,3291,3282,3276,3273,3267,3261,3249,3246,3237,3231,3219,3216,3213,3210,3203,3190,3145,3136,3118,3099,3081,3045,null,null,null,null,null,null,null,null,null],
  /*   7000 ft */ [3260,3268,3277,3285,3293,3300,3298,3295,3292,3285,3284,3278,3276,3272,3260,3255,3248,3240,3235,3232,3228,3222,3212,3210,3190,3168,3125,3114,3103,3092,3082,3060,3025,3018,3003,2989,2975,null,null,null,null,null,null,null,null,null,null],
  /*   8000 ft */ [3210,3218,3227,3235,3243,3250,3250,3250,3247,3238,3237,3230,3228,3224,3209,3203,3195,3186,3180,3175,3165,3145,3106,3097,3067,3048,3009,2999,2989,2979,2970,2950,2912,2905,2890,null,null,null,null,null,null,null,null,null,null,null,null],
  /*   9000 ft */ [3140,3151,3162,3173,3184,3193,3195,3196,3196,3198,3198,3200,3199,3194,3181,3175,3144,3113,3093,3082,3062,3041,3000,2990,2959,2939,2897,2887,2877,2867,2856,2836,2810,null,null,null,null,null,null,null,null,null,null,null,null,null,null],
  /*  10000 ft */ [3025,3045,3064,3084,3104,3119,3123,3127,3131,3143,3145,3150,3145,3130,3079,3059,3029,2998,2978,2968,2948,2927,2887,2877,2846,2826,2786,2776,2765,2755,2745,2725,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null],
  /*  11000 ft */ [2910,2929,2948,2968,2987,3002,3006,3010,3014,3025,3026,3030,3025,3010,2960,2940,2910,2880,2860,2850,2830,2810,2770,2760,2730,2710,2670,2660,2650,2640,2630,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null],
  /*  12000 ft */ [2790,2810,2830,2850,2870,2886,2890,2894,2898,2910,2910,2910,2905,2891,2842,2822,2793,2764,2744,2735,2715,2696,2657,2647,2618,2598,2559,2550,2540,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null],
  /*  13000 ft */ [2680,2698,2717,2735,2753,2768,2772,2775,2779,2790,2789,2786,2785,2771,2725,2707,2680,2652,2634,2625,2606,2588,2552,2542,2515,2497,2460,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null],
  /*  14000 ft */ [2580,2597,2613,2630,2647,2660,2663,2667,2670,2680,2679,2671,2670,2656,2611,2593,2566,2539,2521,2512,2494,2476,2440,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null],
  /*  15000 ft */ [2480,2497,2513,2530,2547,2560,2563,2567,2570,2580,2579,2571,2570,2556,2509,2491,2463,2435,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null,null],
];

/** Carta lista para `consultarCarta`. */
export const CARTA_OGE_BK117_B2 = {
  alts: BK117_OGE_ALTS,
  temps: BK117_OGE_TEMPS,
  rows: BK117_OGE_ROWS,
  procedencia: {
    fuente: "Nomograma OGE del RFM BK117 B-2, digitalizado por el operador (Tablas_20-08-2026.xlsx)",
    verificada: false,
    nota:
      "Convertida de líneas a rejilla. Validada contra el ejemplo de lectura del "
      + "propio gráfico (15 °C / 8000 ft → 3100 kg; la rejilla da 3097 kg). "
      + "Falta cotejo celda por celda contra el RFM.",
  },
};

/**
 * Lo que se encontró mal en los datos de origen y qué se hizo.
 *
 * Queda escrito acá y comprobado en los tests: el día que alguien lo verifique
 * contra el RFM, el test va a fallar y va a obligar a actualizar este registro.
 * Es deliberado — igual que en las cartas del AS350.
 */
export const ANOMALIAS_OGE_BK117 = [
  {
    id: "PA15000-DUPLICADA",
    hallazgo:
      "La etiqueta «PA 15000 ft» traía DOS series distintas, una de 5 puntos y "
      + "otra de 4. La numeración de puntos se reiniciaba, que es lo que delata "
      + "que son dos líneas y no una.",
    evidencia:
      "En el borde izquierdo (-45 °C) la primera daba 2580 kg, exactamente lo "
      + "mismo que PA 14000. La segunda daba 2480 kg, que continúa la caída de "
      + "unos 100 kg por cada 1000 ft que traen 11000, 12000, 13000 y 14000.",
    decision:
      "La primera serie se descartó por ser una segunda digitalización de "
      + "PA 14000. La segunda quedó como PA 15000.",
    verificar: "Cotejar la línea de 15000 ft contra el nomograma del RFM.",
  },
  {
    id: "SERIES-NO-PA",
    hallazgo:
      "El archivo trae tres series que no son líneas de altitud presión: "
      + "«DA 17000 ft (Top Limit)», «Ejemplo Lectura» e «ISA +35C».",
    evidencia:
      "«Ejemplo Lectura» son las líneas guía del ejemplo impreso en el gráfico. "
      + "«ISA +35C» repite los extremos calientes de cada línea PA. "
      + "«DA 17000 ft» es un límite de altitud DENSIDAD, otro eje.",
    decision: "Las tres quedan fuera de la rejilla; meterlas la corrompería.",
    verificar:
      "El límite de DA 17000 ft es una limitación real del tipo y todavía no "
      + "está modelada en la aplicación.",
  },
];
